import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UseQueryResult } from '@tanstack/react-query'

/**
 * A custom hook that wraps a query hook and automatically refetches data when the i18n language changes.
 * This hook preserves the type information from the original query hook.
 *
 * @template TData - The type of data returned by the query
 * @template TError - The type of error returned by the query
 * @template TReturn - The type extending UseQueryResult with the data and error types
 * @param queryHook - The query hook result to wrap
 * @returns The same query hook result with automatic refetching on language change
 */
export function useTranslatedQueryHook<
  TData = unknown,
  TError = unknown,
  TReturn extends UseQueryResult<TData, TError> = UseQueryResult<TData, TError>,
>(queryHook: TReturn): TReturn {
  const { i18n } = useTranslation()
  const [prevLanguageRef, setPrevLanguageRef] = useState(i18n.language)

  useEffect(() => {
    // Only refetch if the language actually changed
    if (prevLanguageRef !== i18n.language && queryHook?.refetch) {
      queryHook.refetch()
      setPrevLanguageRef(i18n.language)
    }
  }, [i18n.language, queryHook, prevLanguageRef])

  return queryHook
}
